import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Menu, MenuItem, Tooltip, makeStyles } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { canAddSeriesCandidate } from './seriesCompatibility'

// `+ Compare` button. On click opens a menu of candidate columns (from
// `candidates` -- normally `config.columns` filtered by same-row scope
// applicability). Each item is evaluated against `seriesCompatibility`
// rules; incompatible items render disabled with their reason as a
// tooltip. Selecting a compatible item calls `onAdd(columnInstance)`.
//
// When the series cap has been reached (or there are no eligible
// candidates) the button itself is disabled with an explanatory
// tooltip so the user understands why nothing happens.

const useStyles = makeStyles(() => ({
  button: {
    height: 24,
    minHeight: 24,
    padding: '0 8px',
    fontSize: 12,
    textTransform: 'none',
    borderRadius: 12,
    color: 'var(--color-primary-700, var(--color-neutral-800, #333))',
    borderColor: 'var(--opal-border-default, #e0e0e0)'
  },
  addIcon: {
    fontSize: 14,
    marginRight: 2
  },
  menuItem: {
    fontSize: 13,
    minHeight: 32
  },
  reason: {
    marginLeft: 12,
    color: 'var(--color-neutral-500, #888)',
    fontSize: 11,
    fontStyle: 'italic'
  }
}))

export default function CompareMenu ({
  candidates,
  primary,
  currentSeries,
  columnDefinitions,
  max,
  onAdd
}) {
  const classes = useStyles()
  const buttonRef = useRef(null)
  const [open, setOpen] = useState(false)

  // Pre-compute each candidate's eligibility once so the menu doesn't
  // re-run the helper on every render of every MenuItem.
  const decorated = useMemo(() => (candidates || []).map(c => ({
    columnInstance: c.columnInstance,
    label: c.label,
    decision: canAddSeriesCandidate({
      primary,
      currentSeries,
      candidate: c.columnInstance,
      columnDefinitions,
      max
    })
  })), [candidates, primary, currentSeries, columnDefinitions, max])

  const atCap = (currentSeries?.length || 0) >= (max || 0)
  const anyEligible = decorated.some(d => d.decision.ok)
  const triggerDisabled = atCap || !anyEligible

  const triggerTooltip = atCap
    ? `Up to ${max} series at a time`
    : (!anyEligible ? 'No compatible measures available' : '')

  const trigger = (
    <span>
      <Button
        ref={buttonRef}
        variant='outlined'
        size='small'
        className={classes.button}
        startIcon={<AddIcon className={classes.addIcon} />}
        onClick={() => setOpen(true)}
        disabled={triggerDisabled}
        aria-haspopup='menu'
        aria-expanded={open ? 'true' : 'false'}
      >
        Compare
      </Button>
    </span>
  )

  return (
    <>
      {triggerTooltip
        ? <Tooltip title={triggerTooltip} placement='top' enterDelay={400}>{trigger}</Tooltip>
        : trigger}
      <Menu
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {decorated.length === 0 && (
          <MenuItem disabled className={classes.menuItem}>
            No other measures in this view
          </MenuItem>
        )}
        {decorated.map(({ columnInstance, label, decision }) => {
          const item = (
            <MenuItem
              key={columnInstance.id}
              className={classes.menuItem}
              disabled={!decision.ok}
              onClick={() => {
                if (!decision.ok) return
                onAdd(columnInstance)
                setOpen(false)
              }}
            >
              <span>{label}</span>
              {!decision.ok && decision.reason && (
                <span className={classes.reason}>{decision.reason}</span>
              )}
            </MenuItem>
          )
          // Tooltip wraps disabled items so the reason still surfaces.
          // MUI swallows tooltips on disabled buttons, so wrap with a span.
          if (!decision.ok && decision.reason) {
            return (
              <Tooltip key={columnInstance.id} title={decision.reason} placement='right' enterDelay={400}>
                <span style={{ display: 'block' }}>{item}</span>
              </Tooltip>
            )
          }
          return item
        })}
      </Menu>
    </>
  )
}

CompareMenu.propTypes = {
  candidates: PropTypes.arrayOf(PropTypes.shape({
    columnInstance: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  primary: PropTypes.object,
  currentSeries: PropTypes.array,
  columnDefinitions: PropTypes.array,
  max: PropTypes.number,
  onAdd: PropTypes.func.isRequired
}
